import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API } from '../constants/api';
import ContactLogo from '../components/shared/ContactLogo';
import ForgotPasswordModal from '../components/shared/ForgotPasswordModal';
import WebGLDotBackground from '../components/shared/WebGLDotBackground';

/* ─── ICONS ──────────────────────────────────────────────── */
const IconEye = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);
const IconEyeOff = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);
const IconGoogle = () => (
  <svg width="18" height="18" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);
/* Reactivado (oct 2026) — el cliente OAuth se había eliminado en Google
   Cloud Console; con uno nuevo configurado vuelve a funcionar. */
const SHOW_GOOGLE_AUTH = true;

const IconUsers = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);

/* ─── LOGIN PAGE ─────────────────────────────────────────── */
const LoginPage = () => {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw]     = useState(false);
  const [error, setError]       = useState(() => {
    // Set by AuthContext's axios interceptor right before it clears an
    // expired session and this page's ProtectedRoute redirect kicks in —
    // without this, an expired JWT silently dumped the user back here with
    // zero explanation, indistinguishable from the app just losing state.
    if (sessionStorage.getItem('cn_session_expired')) {
      sessionStorage.removeItem('cn_session_expired');
      return 'Tu sesión expiró. Inicia sesión nuevamente.';
    }
    // Backend redirects here with ?error=... when Google OAuth fails
    // (google_callback in server.py) — without this it was lost silently.
    const urlError = new URLSearchParams(window.location.search).get('error');
    if (urlError) {
      window.history.replaceState({}, '', window.location.pathname);
      return urlError;
    }
    return '';
  });
  const [loading, setLoading]   = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const { login, user, loading: authLoading } = useAuth();
  const navigate  = useNavigate();
  // Synchronous guard against double-submit: the `disabled={loading}` on the
  // button below only takes effect after React re-renders, so two clicks (or
  // a click plus an Enter keypress) fired in the same tick could both start
  // a login() call before the disabled attribute ever reaches the DOM.
  const submittingRef = useRef(false);

  // Skip the form entirely if a valid session is already sitting in
  // localStorage (e.g. user hit "Iniciar sesión" from the homepage while
  // already logged in elsewhere) — straight to the dashboard instead of
  // asking them to re-enter credentials.
  useEffect(() => {
    if (!authLoading && user) {
      navigate(user.activation_used === false ? '/activate' : '/dashboard', { replace: true });
    }
  }, [authLoading, user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;
    setLoading(true);
    setError('');
    const result = await login(email, password);
    if (result.success) {
      navigate(result.needs_activation ? '/activate' : '/dashboard');
    } else {
      setError(result.error);
    }
    setLoading(false);
    submittingRef.current = false;
  };

  // Still checking localStorage for a valid session, or already found one
  // and about to redirect — render nothing rather than flash the form.
  if (authLoading || user) {
    return <div className="au-root" style={{ background: '#000', minHeight: '100dvh' }} />;
  }

  return (
    <div className="au-root">
      <WebGLDotBackground/>
      <div className="au-wrap">
        <div className="au-card">

          <div className="au-logo">
            <div className="au-logo-mark">
              <ContactLogo clickable/>
            </div>
          </div>

          <div className="au-head">
            <h1 className="au-title">Iniciar sesión</h1>
            <p className="au-sub">Accede a tu cuenta de Contact</p>
          </div>

          <form className="au-form" onSubmit={handleSubmit} noValidate>
            {error && <div className="au-error" role="alert">{error}</div>}

            <div className="au-field">
              <label htmlFor="au-email" className="au-label">Correo electrónico</label>
              <input
                id="au-email" type="email" autoComplete="email" required
                value={email} onChange={e => setEmail(e.target.value)}
                className="au-input" placeholder="tu@empresa.com"
              />
            </div>

            <div className="au-field">
              <div className="au-label-row">
                <label htmlFor="au-password" className="au-label">Contraseña</label>
                <button type="button" className="au-link-sm" onClick={() => setShowForgot(true)}>
                  ¿Olvidaste tu contraseña?
                </button>
              </div>
              <div className="au-pw-wrap">
                <input
                  id="au-password" type={showPw ? 'text' : 'password'}
                  autoComplete="current-password" required
                  value={password} onChange={e => setPassword(e.target.value)}
                  className="au-input au-input--pw" placeholder="••••••••"
                />
                <button
                  type="button" className="au-pw-toggle"
                  onClick={() => setShowPw(v => !v)}
                  aria-label={showPw ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {showPw ? <IconEyeOff/> : <IconEye/>}
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading} className="au-btn-primary" style={{marginTop:4}}>
              {loading ? <span className="au-spinner" aria-label="Cargando"/> : 'Iniciar sesión'}
            </button>

            {SHOW_GOOGLE_AUTH && (
              <>
                <div className="au-divider"><span>o continúa con</span></div>

                <button
                  type="button" disabled={loading} className="au-btn-google"
                  onClick={() => { window.location.href = `${API}/auth/google/login`; }}
                >
                  <IconGoogle/> Google
                </button>
              </>
            )}
          </form>

          <div className="au-footer">
            <p className="au-footer-text">
              ¿No tienes cuenta?{' '}
              <button type="button" className="au-link" onClick={() => navigate('/register')}>
                Regístrate gratis
              </button>
            </p>
            <button type="button" className="au-link-sm" onClick={() => navigate('/team-login')}>
              <IconUsers/> Contact for Teams
            </button>
          </div>

          <button type="button" className="au-btn-back-home" onClick={() => navigate('/')}>
            ← Volver al inicio
          </button>
        </div>
      </div>

      <ForgotPasswordModal show={showForgot} onClose={() => setShowForgot(false)}/>
      <style>{CSS}</style>
    </div>
  );
};

/* ─── CSS ────────────────────────────────────────────────── */
const CSS = `
:root{--au-text:#ffffff;--au-muted:#9a9a9a;--au-faint:#666666;--au-border:#262626;--au-accent:#0071e3;--au-btn-bg:#ededed;--au-btn-text:#0a0a0a}
*{box-sizing:border-box;margin:0;padding:0}

.au-root{font-family:'Inter',system-ui,sans-serif;background:#000;min-height:100dvh;display:flex;flex-direction:column;color:var(--au-text);overflow-x:hidden;position:relative}

/* DOT-GRID WEBGL BACKGROUND */
.au-dotbg{position:fixed;inset:0;z-index:0;pointer-events:none}
.au-dotbg-canvas{position:absolute;inset:0;width:100%;height:100%;display:block}
.au-dotbg-vignette{position:absolute;inset:0;background:radial-gradient(circle at center,rgba(0,0,0,.78) 0%,rgba(0,0,0,0) 100%)}

/* LOGO */

/* LAYOUT */
.au-wrap{position:relative;z-index:2;flex:1;display:flex;align-items:center;justify-content:center;padding:32px 20px}

/* CARD */
.au-card{width:100%;max-width:420px;background:#121212;border:1px solid #222;border-radius:16px;padding:40px 36px;box-shadow:0 10px 50px rgba(0,0,0,.7);animation:auCardIn .7s cubic-bezier(.22,1,.36,1) both}
@keyframes auCardIn{from{opacity:0;transform:translateY(24px) scale(.98)}to{opacity:1;transform:translateY(0) scale(1)}}

/* HEADER */
.au-logo{display:flex;justify-content:center;margin-bottom:32px}
.au-head{margin-bottom:24px}
.au-title{font-size:22px;font-weight:700;line-height:1.2;letter-spacing:-.02em;color:var(--au-text);margin-bottom:5px}
.au-sub{font-size:14px;color:var(--au-muted)}

/* ERROR */
.au-error{background:rgba(220,38,38,.12);border:1px solid rgba(220,38,38,.35);color:#ff6b6b;padding:10px 14px;border-radius:8px;font-size:13px;line-height:1.5}

/* FORM */
.au-form{display:flex;flex-direction:column;gap:16px}
.au-field{display:flex;flex-direction:column;gap:5px}
.au-label{font-size:13px;font-weight:600;color:var(--au-text)}
.au-label-row{display:flex;align-items:center;justify-content:space-between}

/* INPUT */
.au-input{width:100%;height:48px;padding:0 14px;border:1.5px solid var(--au-border);border-radius:10px;font-family:'Inter',system-ui,sans-serif;font-size:15px;color:var(--au-text);background:#000;outline:none;transition:border-color .15s,box-shadow .15s}
.au-input::placeholder{color:var(--au-faint)}
.au-input:focus{border-color:var(--au-accent);box-shadow:0 0 0 3px rgba(0,113,227,.18)}
.au-input--pw{padding-right:46px}
.au-input--mono{font-family:'Menlo','Monaco','Consolas',monospace;font-size:14px;letter-spacing:.06em}

/* PASSWORD TOGGLE */
.au-pw-wrap{position:relative}
.au-pw-toggle{position:absolute;right:0;top:0;width:46px;height:48px;display:flex;align-items:center;justify-content:center;background:none;border:none;color:var(--au-faint);cursor:pointer;transition:color .15s}
.au-pw-toggle:hover{color:var(--au-muted)}

/* HINT */
.au-hint{font-size:12px;color:var(--au-muted);margin-top:2px;line-height:1.5}

/* PRIMARY BUTTON */
.au-btn-primary{width:100%;height:48px;border-radius:100px;background:var(--au-btn-bg);color:var(--au-btn-text);border:none;font-family:'Inter',system-ui,sans-serif;font-size:15px;font-weight:600;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px;transition:opacity .15s,transform .2s cubic-bezier(.22,1,.36,1),box-shadow .2s}
.au-btn-primary:hover{opacity:.87;transform:translateY(-1px);box-shadow:0 6px 20px rgba(255,255,255,.1)}
.au-btn-primary:active{transform:scale(.97);opacity:1;box-shadow:none}
.au-btn-primary:disabled{opacity:.4;cursor:not-allowed;transform:none;box-shadow:none}

/* DIVIDER */
.au-divider{position:relative;text-align:center;padding:2px 0}
.au-divider::before{content:'';position:absolute;top:50%;left:0;right:0;height:1px;background:var(--au-border)}
.au-divider span{position:relative;background:#121212;padding:0 12px;font-size:12px;color:var(--au-faint)}

/* GOOGLE BUTTON */
.au-btn-google{width:100%;height:48px;border-radius:100px;background:transparent;border:1.5px solid var(--au-border);color:var(--au-text);font-family:'Inter',system-ui,sans-serif;font-size:14px;font-weight:500;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:9px;transition:background .15s,border-color .15s}
.au-btn-google:hover{background:rgba(255,255,255,.06);border-color:#3a3a3a}
.au-btn-google:disabled{opacity:.4;cursor:not-allowed}

/* SPINNER */
@keyframes auSpin{to{transform:rotate(360deg)}}
.au-spinner{width:16px;height:16px;border:2px solid rgba(0,0,0,.2);border-top-color:#0a0a0a;border-radius:50%;animation:auSpin .55s linear infinite;flex-shrink:0}

/* FOOTER */
.au-footer{margin-top:24px;display:flex;flex-direction:column;align-items:center;gap:10px}
.au-footer-text{font-size:13px;color:var(--au-muted);text-align:center}
.au-link{font-weight:600;color:var(--au-text);background:none;border:none;cursor:pointer;font-family:inherit;font-size:inherit;transition:opacity .15s;padding:0}
.au-link:hover{opacity:.6}
.au-link-sm{font-size:12px;color:var(--au-muted);background:none;border:none;cursor:pointer;font-family:inherit;transition:color .15s;display:inline-flex;align-items:center;gap:5px;padding:0}
.au-link-sm:hover{color:var(--au-text)}
.au-btn-back-home{display:block;width:100%;margin-top:20px;background:none;border:none;cursor:pointer;font-family:inherit;font-size:12px;color:var(--au-faint);text-align:center;padding:0;transition:color .15s}
.au-btn-back-home:hover{color:var(--au-muted)}

/* TEAM BADGE */
.au-team-badge{display:inline-flex;align-items:center;gap:8px;border:1px solid var(--au-border);border-radius:100px;padding:6px 14px;font-size:12px;color:var(--au-muted);background:rgba(255,255,255,.04)}

/* BACK */
.au-back{display:inline-flex;align-items:center;gap:6px;font-size:13px;color:var(--au-muted);background:none;border:none;cursor:pointer;font-family:inherit;transition:color .15s;padding:0;margin-bottom:20px}
.au-back:hover{color:var(--au-text)}

@media(max-width:480px){
  .au-wrap{padding:12px}
  .au-card{padding:24px 20px;border-radius:14px}
  .au-logo{margin-bottom:16px}
  .au-logo-mark img{height:26px}
  .au-head{margin-bottom:14px}
  .au-title{font-size:19px}
  .au-sub{font-size:12.5px}
  .au-form{gap:11px}
  .au-field{gap:4px}
  .au-input{height:42px;font-size:14px}
  .au-pw-toggle{height:42px}
  .au-btn-primary,.au-btn-google{height:42px;font-size:14px}
  .au-footer{margin-top:14px;gap:6px}
  .au-btn-back-home{margin-top:12px}
}
@media(prefers-reduced-motion:reduce){
  *{animation-duration:.01ms !important;transition-duration:.01ms !important}
}
`;

export default LoginPage;
